import moment from 'moment';
import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useObjectVal } from 'react-firebase-hooks/database';
import {
  useFilters,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table';
import {
  Button,
  Col,
  Collapse,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { AbilityContext, Can } from '../../auth/Can';
import {
  ArchiveIcon,
  DeleteIcon,
  PauseIcon,
  PlayIcon,
  UnarchiveIcon,
} from '../../components/Icons';
import { Loader } from '../../components/Loader';
import { ColumnsSelector } from '../../components/table/ColumnsSelector';
import { DefaultColumnFilter } from '../../components/table/DefaultFilter';
import { logger } from '../../logging';
import { getDAMApp } from '../../services/firebase';
import {
  State,
  WorkflowState,
  WorkflowSubtypeMap,
} from './types';
import { WorkflowModal } from './WorkflowModal';
import { WorkflowsTable } from './WorkflowsTable';
import {
  descriptionColumnFilterFn,
  getDescriptionSearchValue,
  getReadableWorkflowLifecycleStage,
} from './utils';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref } from '../../utils/firebase';
import { equalTo, orderByChild, query, update, get } from 'firebase/database';
import { ExportToExcel } from '../accounts/ExportToExcel';
const R = require('ramda');

const pagesStyles = require('../pages.css');
const workflowsStyles = require('./workflows.css');
const iconStyles = require('../../components/icons.css');

const now = moment().unix();

const IFB_HOSTING_DOMAIN_URL = process.env.FIREBASE_IFB_DOMAIN_URL;
const WEBFORM_LINE_PREFIX = 'Webform:';

const renderWorkflowDescription = (
  description: string | undefined,
  formId?: string
) => {
  if (!description) {
    return null;
  }

  return description.split('\n').map((line, index) => {
    if (
      line.startsWith(WEBFORM_LINE_PREFIX) &&
      formId &&
      IFB_HOSTING_DOMAIN_URL
    ) {
      const formTitle = line
        .slice(WEBFORM_LINE_PREFIX.length)
        .trim()
        .replace(/\]$/, '');
      return (
        <div key={index}>
          {WEBFORM_LINE_PREFIX}{' '}
          <a
            href={`${IFB_HOSTING_DOMAIN_URL}/builder/form/${formId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {formTitle || formId}
          </a>
        </div>
      );
    }

    return <div key={index}>{line}</div>;
  });
};

// Sort key for workflows that have never run (sorts below any real unix timestamp)
const NO_EXECUTION_SORT_VALUE = 0;

// Green = action available; red = action blocked (visual cue for operators)
const getActionButtonClass = (disabled: boolean) =>
  `${workflowsStyles.actionButton} ${
    disabled
      ? workflowsStyles.actionButtonDisabled
      : workflowsStyles.actionButtonEnabled
  }`;

/** Archived/deleted workflows have no scheduler — exclude from Errored tab and block state actions. */
const isArchivedOrDeletedWorkflow = (workflow: {
  isArchived?: boolean;
  status?: string;
}) =>
  workflow.isArchived === true || workflow.status === State.DELETED;

const filterWorkflowsForTab = (
  workflowsList: Record<string, any>,
  tab: { archived?: boolean; errored?: boolean }
) =>
  Object.keys(workflowsList).filter((k) => {
    const v = workflowsList[k];

    if (tab.archived === true && v.isArchived) {
      return v;
    }
    // Errored tab keys off last_execution_result only — exclude archived (status deleted, no scheduler)
    if (
      tab.errored === true &&
      v.last_execution_result === 'error' &&
      !v.isArchived
    ) {
      return v;
    }
    // Active tab: not archived and not in a terminal error state
    if (
      !v.isArchived &&
      v.last_execution_result !== 'error' &&
      v.last_execution_result !== 'retrying'
    ) {
      return v;
    }
  });

export const Workflows = ({
  archived,
  errored,
}: {
  archived?: boolean;
  errored?: boolean;
}) => {
  const damApp = getDAMApp();
  const functions = getFunctions(damApp);
  const damAuth = getAuth(damApp);
  const TABLE_NAME = 'workflows';
  const [user, userLoading] = useAuthState(damAuth as any);

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState(null);
  const toggleDeleteModal = () => {
    setDeleteModal(!deleteModal);
    if (deleteModal === false) {
      workflowToDelete(null);
    }
  };

  const workflowsRef = ref('workflows');
  const workflowsArchivedQuery = query(
    workflowsRef,
    orderByChild('isArchived'),
    equalTo(true)
  );

  const workflowsErroredQuery = query(
    workflowsRef,
    orderByChild('last_execution_result'),
    equalTo('error')
  );

  let workflowsExportFilenamePrefix = 'active';

  const workflowsActiveQuery = workflowsRef;
  // because apparently workflows don't default isArchived to false
  let workflowsQuery;
  if (archived === true) {
    workflowsQuery = workflowsArchivedQuery;
    workflowsExportFilenamePrefix = 'archived';
  } else if (errored === true) {
    workflowsQuery = workflowsErroredQuery;
    workflowsExportFilenamePrefix = 'errored';
  } else {
    workflowsQuery = workflowsActiveQuery;
  }

  const [workflowsList, loading, error] = useObjectVal(workflowsQuery as any);
  const [values, setValues] = useState([]);
  const [valuesLoading, setValuesLoading] = useState(false);

  const [isColumnsSelectOpen, setIsColumnsSelectOpen] = useState(false);
  const uid = damAuth.currentUser.uid;
  const userColsRef = ref(`user_preferences/${uid}/columns/${TABLE_NAME}`);
  const [userCols, userColsLoading, userColsError] = useObjectVal(
    userColsRef as any
  );

  const [workflow, setWorkflow] = useState(null);
  const [isWorkflowModalOpen, setIsWorkflowModalOpen] = useState(false);

  const ability = useContext(AbilityContext);
  const isWorkflowsAdmin = ability.can('manage', 'workflows_admin');
  const isPrivacyWorkflowsAdmin = ability.can('manage', 'privacy_workflows');

  // when workflowsList changes
  useEffect(() => {
    if (isWorkflowModalOpen === true) {
      return;
    }

    let isMounted = true;
    if (workflowsList === null) {
      if (isMounted) {
        setValuesLoading(true);
        setValues([]);
      }
    }

    if (workflowsList) {
      const workflows = filterWorkflowsForTab(workflowsList, {
        archived,
        errored,
      });

      if (isMounted) {
        setValuesLoading(true);

        const data = workflows.map((k) => {
          const value = workflowsList[k];
          const labels = { ...value };
          return { ...labels, key: k };
        });

        setValues(data);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [workflowsList, isWorkflowModalOpen, archived, errored]);

  useEffect(() => {
    if (isWorkflowModalOpen === false) {
      logger.debug(`workflowModal has been closed. now reload the values`);

      let isMounted = true;
      if (workflowsList) {
        const workflows = filterWorkflowsForTab(workflowsList, {
          archived,
          errored,
        });

        if (isMounted) {
          setValuesLoading(true);
          setValues(
            workflows.map((k) => {
              const value = workflowsList[k];
              const labels = { ...value };
              return { ...labels, key: k };
            })
          );
        }
      }

      return () => {
        isMounted = false;
      };
    }
  }, [isWorkflowModalOpen, workflowsList, archived, errored]);

  useEffect(() => {
    setValuesLoading(false);
  }, [values]);

  const updateData = async (
    rowIndex,
    columnId,
    value,
    updateTableData,
    key
  ) => {
    const workflowsRef = ref(`workflows/${key}`);
    const workflow = {};
    workflow[columnId] = value;
    workflow['modified'] = now;
    try {
      await update(workflowsRef, workflow);
    } catch (e) {
      logger.error('[updateMyData] - error updating workflow row: ', e);
    }

    if (updateTableData) {
      logger.debug(`setting values 3`);
      setValues((old) =>
        old.map((row, index) => {
          if (index === rowIndex) {
            return {
              ...old[rowIndex],
              ...workflow,
            };
          }
          return row;
        })
      );
    }
  };

  const handleStateChange = async (
    state: WorkflowState,
    workflowid: string
  ) => {
    try {
      logger.debug(
        `[handleStateChange] [${workflowid}] changing to state: ${state}`
      );
      const callable = httpsCallable(
        functions,
        'accountManager-callableUpdateWorkflowState'
      );
      await callable({ id: workflowid, state });
    } catch (e) {
      logger.error(
        `[handleStateChange] - error changing state for workflow id: ${workflowid} to state: ${state} - ${e}`
      );
    }
  };

  const handleDeleteWorkflow = (rowIndex: number, workflowid: string) => {
    logger.debug(
      `[handleDeleteWorkflow] rowIndex: ${rowIndex} workflowid: ${workflowid}`
    );
    // open modal
    setWorkflowToDelete({ rowIndex, workflowid });
    setDeleteModal(true);
  };

  const unarchiveWorkflow = async (rowIndex: number, key: string) => {
    logger.debug(`[unarchiveWorkflow] rowIndex: ${rowIndex} `);
    await updateData(rowIndex, 'isArchived', false, true, key);
  };

  // #1433 - this needs to be a new callable as it will need to delete the cloud scheduler
  const archiveWorkflow = async (rowIndex: number, key: string) => {
    logger.info(`[archiveWorkflow] rowIndex: ${rowIndex} `);

    const callable = httpsCallable(
      functions,
      'accountManager-callableArchiveWorkflow'
    );

    await callable({ id: key });

    await updateData(rowIndex, 'isArchived', true, true, key);
  };

  const deleteWorkflow = async () => {
    setDeleting(true);
    const { rowIndex, workflowid } = workflowToDelete;
    logger.debug(
      `[deleteWorkflow] rowIndex: ${rowIndex} workflowid: ${workflowid}`
    );
    // delete the workflow
    try {
      const callable = httpsCallable(
        functions,
        'accountManager-callableDeleteWorkflow'
      );
      await callable(workflowid);
    } catch (e) {
      logger.error(
        `[deleteWorkflow] - error deleting workflow id: ${workflowid} - ${e}`
      );
    }

    const data = [...values];
    data.splice(rowIndex, 1);
    logger.debug(`setting values 4 (delete)`);
    setValues(data);
    setDeleting(false);
    setDeleteModal(false);
  };

  const handleRowClick = (row, cell, e) => {
    logger.debug(
      `[handleRowClick] row: ${JSON.stringify(row.original)} cell: ${
        cell.column.id
      }`
    );
    if (cell.column.id === 'actions') {
      return;
    }

    setWorkflow(row.original);
    setIsWorkflowModalOpen(true);
  };

  const RenderBooleanCell = ({
    cell: { value: initialValue },
    row: { index, original },
    column: { id },
    updateData,
  }) => {
    const [value, setValue] = React.useState(initialValue);
    return value === 'true' ? 'True' : 'False';
  };

  const columns = React.useMemo(
    () => [
      {
        Header: 'ID',
        accessor: 'key',
        id: 'id',
      },
      {
        Header: 'Name',
        accessor: 'name',
        id: 'name',
      },
      ...(archived === false
        ? [
            {
              Header: 'Actions',
              id: 'actions',
              Cell: ({ row }) => {
                const [loading, setLoading] = useState(false);
                const status = row.original.status;
                const isArchivedOrDeleted = isArchivedOrDeletedWorkflow(
                  row.original
                );
                const isPrivacy =
                  (row.original.args && row.original.args.is_privacy) || false;
                const createdByEmail =
                  row.original.createdBy && row.original.createdBy.email;
                const canResume =
                  isPrivacy === false ||
                  (isPrivacy === true &&
                    user.email !== createdByEmail &&
                    isPrivacyWorkflowsAdmin);
                const resumeDisabled =
                  status === State.ENABLED ||
                  !canResume ||
                  // Archived/deleted workflows have no Cloud Scheduler job to resume
                  isArchivedOrDeleted;
                const deleteDisabled = status === State.ENABLED ? true : false;
                const pauseDisabled = status !== State.ENABLED ? true : false;
                const runNowDisabled =
                  // Allow run-now from paused (errored) as well as enabled
                  (status !== State.ENABLED && status !== State.PAUSED) ||
                  isArchivedOrDeleted;

                const handleStateChangeClick = async (
                  state: WorkflowState,
                  workflowid: string
                ) => {
                  setLoading(true);
                  await handleStateChange(state, workflowid);
                  setLoading(false);
                };

                const canArchive = isWorkflowsAdmin && status !== State.ENABLED;

                return (
                  <div className={workflowsStyles.actionsRow}>
                    <Button
                      className={getActionButtonClass(resumeDisabled)}
                      title={
                        isArchivedOrDeleted
                          ? 'Cannot resume — workflow is archived'
                          : 'Resume'
                      }
                      disabled={resumeDisabled}
                      onClick={() =>
                        handleStateChangeClick(
                          WorkflowState.RESUME,
                          row.original.key
                        )
                      }
                    >
                      <PlayIcon fill="white" class={iconStyles.tick} />
                    </Button>

                    <Button
                      className={getActionButtonClass(pauseDisabled)}
                      title="Pause"
                      disabled={pauseDisabled}
                      onClick={() =>
                        handleStateChangeClick(
                          WorkflowState.PAUSE,
                          row.original.key
                        )
                      }
                    >
                      <PauseIcon fill="white" class={iconStyles.tick} />
                    </Button>

                    <Button
                      className={getActionButtonClass(runNowDisabled)}
                      title={
                        isArchivedOrDeleted
                          ? 'Cannot run — workflow is archived'
                          : 'Run Now'
                      }
                      disabled={runNowDisabled}
                      onClick={() =>
                        handleStateChangeClick(
                          WorkflowState.RUN_NOW,
                          row.original.key
                        )
                      }
                    >
                      {'Run Now'}
                    </Button>

                    <Button
                      className={getActionButtonClass(deleteDisabled)}
                      title="Delete"
                      disabled={deleteDisabled}
                      onClick={() =>
                        handleDeleteWorkflow(row.index, row.original.key)
                      }
                    >
                      <DeleteIcon fill="white" class={iconStyles.tick} />
                    </Button>

                    <Can I="manage" a="workflows_admin">
                      <Button
                        className={getActionButtonClass(!canArchive)}
                        title="Archive"
                        onClick={() =>
                          archiveWorkflow(row.index, row.original.key)
                        }
                        disabled={!canArchive}
                      >
                        <ArchiveIcon fill="white" class={iconStyles.archive} />
                      </Button>
                    </Can>

                    <Loader loading={loading} />
                  </div>
                );
              },
            },
          ]
        : []),
      {
        Header: 'Last Execution',
        id: 'last_execution',
        // Numeric accessor enables descending sort on Active tab; Cell renders human-readable label
        accessor: (row) => {
          if (row.last_execution) {
            return parseInt(row.last_execution, 10);
          }
          // Never-run workflows sort by created so new items appear at the top
          if (row.created) {
            return parseInt(row.created, 10);
          }

          return NO_EXECUTION_SORT_VALUE;
        },
        Cell: ({
          row,
        }: {
          row: { original: { last_execution?: string; status?: string } };
        }) => {
          if (row.original.last_execution) {
            return moment
              .unix(parseInt(row.original.last_execution, 10))
              .format('YYYY-MM-DD HH:mm:ss');
          }

          const isActiveTab = archived !== true && errored !== true;
          if (isActiveTab && row.original.status !== State.PAUSED) {
            return 'Scheduled';
          }

          return 'Has not executed';
        },
      },
      {
        Header: 'Last Execution Result',
        accessor: (row) => {
          // Backend no longer writes 'retrying'; map legacy values for display
          if (row.last_execution_result === 'retrying') {
            return 'running';
          }
          if (row.last_execution_result) {
            return row.last_execution_result;
          }

          return '';
        },
        id: 'last_execution_result',
      },
      {
        // Replaces legacy "Lifecycle Stage": all standard types run daily until archived
        Header: 'Daily Schedule',
        accessor: getReadableWorkflowLifecycleStage,
        id: 'lifecycle_stage',
      },
      {
        Header: 'Scheduler Status',
        accessor: 'status',
        id: 'status',
      },
      {
        Header: 'Error',
        accessor: (row) =>
          row.last_execution_result === 'error' ? row.error : '',
        id: 'error',
      },
      {
        Header: 'Created',
        accessor: (row) => {
          if (row.created) {
            return moment
              .unix(parseInt(row.created))
              .format('YYYY-MM-DD HH:mm:ss');
          }

          return '';
        },
        id: 'created',
      },
      {
        Header: 'Created By',
        accessor: (row) => {
          if (row.createdBy) {
            return row.createdBy.email;
          }

          return '';
        },
        id: 'createdBy',
        hideHeader: true,
      },
      {
        Header: 'Modified',
        accessor: (row) => {
          if (row.modified) {
            return moment
              .unix(parseInt(row.modified))
              .format('YYYY-MM-DD HH:mm:ss');
          }

          return '';
        },
        id: 'modified',
      },
      {
        Header: 'Subtype',
        accessor: (row) => {
          const type = WorkflowSubtypeMap[row.subtype];
          return type || '';
        },
        id: 'subtype',
      },
      {
        Header: 'Description',
        accessor: (row) => getDescriptionSearchValue(row),
        Cell: ({ row }) =>
          renderWorkflowDescription(
            row.original.description,
            row.original.args?.form_id
          ),
        id: 'description',
        filter: descriptionColumnFilterFn,
        className: workflowsStyles.descriptionColumn,
      },
      {
        Header: 'Privacy',
        accessor: (row) => {
          if (row.args && row.args.is_privacy === true) {
            return 'true';
          }

          return 'false';
        },
        Cell: RenderBooleanCell,
        id: 'is_privacy',
      },
      {
        Header: 'Store',
        accessor: (row) => {
          if (row.args && row.args.is_store === true) {
            return 'true';
          }

          return 'false';
        },
        Cell: RenderBooleanCell,
        id: 'is_store',
      },
    ],
    [archived, errored]
  );

  const filterTypes = React.useMemo(
    () => ({}),
    []
  );

  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  );

  // memoize values - the heavy lifting is now done in the above useEffect
  const records = React.useMemo(() => values, [values]);

  let hiddenColumns: any = R.keys(
    R.pickBy((val, key) => val === false, userCols)
  );

  const isActiveTab = archived !== true && errored !== true;

  const tableData = useTable(
    {
      columns,
      data: records,
      filterTypes,
      defaultColumn,
      autoResetFilters: false,
      autoResetPage: false,
      autoResetSortBy: false,
      initialState: {
        // Active tab: most recently executed workflows first; other tabs keep created desc
        sortBy: isActiveTab
          ? [{ id: 'last_execution', desc: true }]
          : [{ id: 'created', desc: true }],
        filters: [
          {
            id: 'isArchived',
            value: 'false',
          },
        ],
        pageSize: 20,
        pageIndex: 0,
      },
      autoResetHiddenColumns: false,
      updateData,
      handleRowClick,
    } as any,
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  ) as any;

  React.useEffect(() => {
    hiddenColumns = R.keys(R.pickBy((val, key) => val === false, userCols));
    tableData.setHiddenColumns(hiddenColumns);
  }, [userCols]);

  if (loading || valuesLoading || userColsLoading || userLoading) {
    return <Loader loading={true} />;
  }

  if (error) {
    return <div>{`Error: ${error.message}`}</div>;
  }

  if (userColsError) {
    return <div>Error loading cols</div>;
  }

  if (!user) {
    return <div>Error loading current user</div>;
  }

  const toggleColumnsSelect = () =>
    setIsColumnsSelectOpen(!isColumnsSelectOpen);
  const toggleWorkflowModal = () =>
    setIsWorkflowModalOpen(!isWorkflowModalOpen);

  return (
    <div className={pagesStyles.fullHeight}>
      {values && (
        <div className={`${pagesStyles.noPadding} ${pagesStyles.fullHeight}`}>
          <Row className={pagesStyles.headerRow}>
            <Col sm={{ size: 12 }} className={pagesStyles.noPadding}>
              <Button
                onClick={toggleWorkflowModal}
                className={workflowsStyles.topButtons}
              >
                Create New Workflow
              </Button>
              <Button
                onClick={toggleColumnsSelect}
                className={workflowsStyles.topButtons}
              >
                Columns
              </Button>
              <Can I="manage" a="export">
                <ExportToExcel
                  csvData={tableData.rows.map((row) => row.values) || []}
                  fileName={`${workflowsExportFilenamePrefix}-workflows-export-csv-${moment().format(
                    'DD-MM-YYYY hh:mm:ss'
                  )}`}
                  user={null}
                  label="Export Tab"
                />
              </Can>
            </Col>
          </Row>
          <Row>
            <Col sm={8}>
              <Collapse isOpen={isColumnsSelectOpen}>
                <ColumnsSelector
                  columns={columns}
                  userCols={userCols}
                  tableName={TABLE_NAME}
                />
              </Collapse>
            </Col>
          </Row>
          <Row className={pagesStyles.fullHeight}>
            <Col sm={{ size: 12 }} className={pagesStyles.fullHeight}>
              <Can I="manage" a="workflows">
                <WorkflowsTable {...tableData} />
              </Can>
            </Col>
          </Row>
          <Row>
            <Col sm={{ size: 12 }}>
              <Can I="manage" a="workflows">
                <Modal isOpen={deleteModal} toggle={(e) => toggleDeleteModal()}>
                  <ModalHeader toggle={(e) => toggleDeleteModal()}>
                    Please Confirm
                  </ModalHeader>
                  <ModalBody>
                    {`Please confirm that you'd like to delete workflow: ${
                      (workflowToDelete &&
                        workflowToDelete.workflowid &&
                        workflowToDelete.workflowid) ||
                      ''
                    }`}
                  </ModalBody>
                  <ModalFooter>
                    <Button
                      color="primary"
                      onClick={() => deleteWorkflow()}
                      disabled={deleting}
                    >
                      Confirm
                    </Button>{' '}
                    <Button
                      color="secondary"
                      onClick={(e) => toggleDeleteModal()}
                      disabled={deleting}
                    >
                      Cancel
                    </Button>
                    <Loader loading={deleting} />
                  </ModalFooter>
                </Modal>
              </Can>
            </Col>
          </Row>
        </div>
      )}
      <Can I="manage" a="workflows">
        {isWorkflowModalOpen && (
          <Row className={pagesStyles.fullHeight}>
            <Col sm={{ size: 12 }} className={pagesStyles.fullHeight}>
              <WorkflowModal
                workflow={workflow}
                setWorkflow={setWorkflow}
                isOpen={isWorkflowModalOpen}
                toggle={setIsWorkflowModalOpen}
                user={user}
                isWorkflowsAdmin={isWorkflowsAdmin}
              />
            </Col>
          </Row>
        )}
      </Can>
    </div>
  );
};
